import { useEffect, useState } from 'react'
import type { ComponentType, KeyboardEvent, SVGProps } from 'react'
import { ArrowUpRight, FileClock, MedChron, MedicalBillsNew } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { MatterRecordSelectionCard, type MatterRecordSelectionCardType } from './MatterRecordSelectionCard'

type RecordTypeOption = {
  recordType: MatterRecordSelectionCardType
  title: string
  outputLabel: string
  description: string
  tag?: boolean
  ExistingRecordIcon: ComponentType<SVGProps<SVGSVGElement>>
}

const RECORD_TYPE_OPTIONS: RecordTypeOption[] = [
  {
    recordType: 'timeline',
    title: 'Case Documents',
    outputLabel: 'Case Timeline',
    tag: true,
    description: 'Dated documents from the case, such as reports, statements, correspondences, and filings.',
    ExistingRecordIcon: FileClock,
  },
  {
    recordType: 'chronology',
    title: 'Medical Records',
    outputLabel: 'Medical Chronology',
    description: 'Treatment records, such as provider notes, hospital records, lab reports, and discharge summaries.',
    ExistingRecordIcon: MedChron,
  },
  {
    recordType: 'billing',
    title: 'Medical Bills',
    outputLabel: 'Medical Billing Summary',
    description: 'Medical bills, such as EOBs, billing ledgers, and itemized receipts from providers.',
    ExistingRecordIcon: MedicalBillsNew,
  },
]

export interface BuildMatterRecordModalProps {
  /** Controlled modal visibility. */
  open: boolean
  /** Receives Radix dismissals and explicit Cancel actions. */
  onOpenChange: (open: boolean) => void
  /** Reports the selected available record type. Host performs routing and generation. */
  onContinue: (selectedRecordType: MatterRecordSelectionCardType) => void
  /** Reports a card whose selection changed, including arrow keys. The same card does not report again. */
  onRecordTypeSelected?: (recordType: MatterRecordSelectionCardType) => void
  /**
   * Record types already generated for this matter.
   * Excluded from the choices and shown as actionable chips below the selection list.
   * Default: [].
   */
  existingRecordTypes?: MatterRecordSelectionCardType[]
  /**
   * Opens an existing matter record. Required because the existing-record row is an
   * actionable control — callers without existing records pass no `existingRecordTypes`
   * and this callback remains unused.
   */
  onViewExistingRecord: (recordType: MatterRecordSelectionCardType) => void
}

/**
 * Modal for selecting which matter record to build.
 *
 * Presents Case Timeline, Medical Chronology, and Medical Billing Summary as choices.
 * Manages one selected record type and reports it via `onContinue`. Record types already
 * generated for the matter are removed from the choices and rendered as actionable chips.
 *
 * The host owns navigation and analytics. This modal reports a changed selection and does not
 * navigate or start generation.
 *
 * Figma: https://www.figma.com/design/dXICWmcPx8xc8DqnJH83No/Medical-Chronology---Billing-Summary?node-id=11773-113502
 */
export function BuildMatterRecordModal({
  open,
  onOpenChange,
  onContinue,
  onRecordTypeSelected,
  existingRecordTypes = [],
  onViewExistingRecord,
}: BuildMatterRecordModalProps) {
  const [selectedRecordType, setSelectedRecordType] = useState<MatterRecordSelectionCardType | null>(null)

  // Reset selection each time the modal opens so every session starts clean.
  useEffect(() => {
    if (open) setSelectedRecordType(null)
  }, [open])

  const availableOptions = RECORD_TYPE_OPTIONS.filter(({ recordType }) => !existingRecordTypes.includes(recordType))
  const existingOptions = RECORD_TYPE_OPTIONS.filter(({ recordType }) => existingRecordTypes.includes(recordType))

  // Derive active selection: null if the selected type was removed from available options.
  const activeSelection = availableOptions.some(({ recordType }) => recordType === selectedRecordType) ? selectedRecordType : null

  // Roving tabindex: selected card gets 0; first card gets 0 when nothing is selected.
  const focusedIndex = activeSelection !== null ? availableOptions.findIndex(({ recordType }) => recordType === activeSelection) : 0

  function selectRecordType(recordType: MatterRecordSelectionCardType) {
    if (recordType === activeSelection) return
    setSelectedRecordType(recordType)
    onRecordTypeSelected?.(recordType)
  }

  function handleRadioGroupKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return
    e.preventDefault()
    const count = availableOptions.length
    if (count === 0) return
    const radios = e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')
    // Prefer the focused element's index so Tab → Arrow advances correctly even before
    // the user has made a selection (findIndex returns -1 when activeSelection is null,
    // which would incorrectly keep navigation pinned to index 0).
    const focusedDomIndex = Array.from(radios).indexOf(document.activeElement as HTMLElement)
    const currentIndex = focusedDomIndex !== -1 ? focusedDomIndex : availableOptions.findIndex(({ recordType }) => recordType === activeSelection)
    const nextIndex =
      e.key === 'ArrowDown' || e.key === 'ArrowRight'
        ? currentIndex === -1
          ? 0
          : (currentIndex + 1) % count
        : currentIndex <= 0
        ? count - 1
        : currentIndex - 1
    const nextOption = availableOptions[nextIndex]
    if (!nextOption) return
    selectRecordType(nextOption.recordType)
    radios[nextIndex]?.focus()
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        width="xl"
        height="max-xl"
        className="max-w-[734px]"
        title="What documents do you want to organize?"
        subtitle="Each option produces a different type of table output. Only one of each matter record type can be generated per matter."
        showCloseButton
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false) }}
            primaryAction={{
              label: 'Continue',
              disabled: activeSelection === null,
              onClick: () => {
                if (activeSelection) onContinue(activeSelection)
              },
            }}
          />
        }
      >
        <div className="flex flex-col gap-4">
          {availableOptions.length > 0 && (
            <div role="radiogroup" aria-label="Matter record type" className="flex flex-col gap-3 p-1" onKeyDown={handleRadioGroupKeyDown}>
              {availableOptions.map((option, index) => (
                <MatterRecordSelectionCard
                  key={option.recordType}
                  recordType={option.recordType}
                  title={option.title}
                  outputLabel={option.outputLabel}
                  description={option.description}
                  tag={option.tag}
                  selected={activeSelection === option.recordType}
                  onSelect={() => selectRecordType(option.recordType)}
                  tabIndex={index === focusedIndex ? 0 : -1}
                />
              ))}
            </div>
          )}
          {existingOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-brand-neutral-950">Already in this matter:</span>
              {existingOptions.map((option) => (
                <button
                  key={option.recordType}
                  type="button"
                  className="flex items-center gap-1.5 rounded border border-brand-neutral-200 bg-white px-2 py-1 text-sm font-medium text-brand-neutral-800 transition-colors hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                  onClick={() => onViewExistingRecord(option.recordType)}
                >
                  <option.ExistingRecordIcon className="size-4 shrink-0" aria-hidden />
                  <span>{option.outputLabel}</span>
                  <ArrowUpRight className="size-4 shrink-0" aria-hidden />
                </button>
              ))}
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}
